import { forwardRef, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink, RouterProvider } from "react-router-dom"

import { mode } from "rtk-slice"

import { CssBaseline, GlobalStyles } from "@mui/material"
import { grey } from "@mui/material/colors"
import { createTheme, ThemeProvider } from "@mui/material/styles"

import { SnackbarProvider } from "notistack"

import { router } from "./routes/router"

import "./App.scss"

const LinkBehavior = forwardRef((props, ref) => {
  const { href, ...other } = props
  return <RouterLink ref={ref} to={href} {...other} />
})

LinkBehavior.displayName = "LinkBehavior"

export default function App() {
  const modeState = useSelector(mode)

  const themeMode = useMemo(() => {
    let mode
    if (modeState === "system default") {
      matchMedia("(prefers-color-scheme: dark)").matches
        ? (mode = "dark")
        : (mode = "light")
    }
    if (modeState === "light") {
      mode = "light"
    }
    if (modeState === "dark") {
      mode = "dark"
    }
    return mode
  }, [modeState])

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          htmlFontSize: 11,
          tooltip: {
            fontSize: 10,
          },
        },
        palette: {
          mode: themeMode,
          ...(themeMode === "light"
            ? {
                primary: {
                  main: "#679186",
                },
                divider: grey[500],
                text: {
                  primary: grey[900],
                  secondary: grey[800],
                  cancel: grey[600],
                },
                background: {
                  default: grey[50],
                  paper: "#fcefff",
                  card: "#dde7fa",
                  header: "#ffffff",
                  cardHeader: "#ffffff",
                  settings: "#dde7fa",
                },
                paper: {
                  primary: "#040303",
                },
              }
            : {
                primary: {
                  main: "#b3c700",
                },
                secondary: {
                  main: "#c51162",
                },
                divider: grey[800],
                background: {
                  default: "#121212",
                  card: "#191919",
                  cardHeader: "#121212",

                  paper: "#000E10",
                  confirm: "#000E14",
                  settings: "#000000dd",
                  header: "#000000f1",
                },
                text: {
                  primary: "#fff",
                  secondary: grey[100],
                  cancel: grey[500],
                },
                selected: "whitesmoke",
              }),
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                "& .MuiCardHeader-content": {
                  maxWidth: "90%",
                },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                "&.MuiTooltip-tooltipPlacementLeft": {
                  width: "8em",
                  textAlign: "center",
                },
              },
            },
          },
          MuiSpeedDial: {
            styleOverrides: {
              root: {
                "& .MuiSpeedDialAction-staticTooltip .MuiSpeedDialAction-staticTooltipLabel":
                  {
                    textAlign: "center",
                    width: "8em !important",
                  },
              },
            },
          },
          MuiButtonBase: {
            defaultProps: {
              LinkComponent: LinkBehavior,
            },
          },
          MuiBadge: {
            styleOverrides: {
              badge: {
                color: "white !important",
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: "background.paper",
                backgroundImage: "none",
              },
            },
          },
        },
      }),
    [themeMode]
  )

  useEffect(() => {
    function stopContextMenu(e) {
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener("contextmenu", stopContextMenu)
    return () =>
      window.removeEventListener("contextmenu", stopContextMenu)
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        Components="success"
        variant="success"
        style={{
          fontSize: "1.5rem",
          backgroundColor: "#1A4D2E",
        }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      />
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            position: "relative",
            userSelect: "none",
          },
          "#root": {
            minHeight: "100vh",
          },
        }}
      />

      <RouterProvider router={router} />
    </ThemeProvider>
  )
}
